import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert
} from "@mui/material";
import { Grid as GridJS } from "gridjs-react";
import "gridjs/dist/theme/mermaid.css";
import { Chart } from "react-google-charts";
import apiService from "../components/ReasuableFuncations/api";
import pptxgen from "pptxgenjs";

const SDataCollection = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectSummary, setProjectSummary] = useState({
    totalProjects: 0,
    totalOngoing: 0,
    totalPending: 0,
    totalCompleted: 0
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (!userData) {
        setError("User data not found. Please log in.");
        setLoading(false);
        return;
      }

      const { 
        selectedOption, 
        regionalNetworkOption, 
        memberStateOption, 
        countryOption 
      } = userData;
      const formType = "activity";
      const url = "/api/data-collection";

      setLoading(true);
      try {
        let response;

        if (selectedOption === "Comesa") {
          response = await apiService.get(`${url}?type=${formType.toLowerCase()}`);
        } else if (selectedOption === "Regional Network" && regionalNetworkOption) {
          response = await apiService.get(`${url}?regionalNetwork=${regionalNetworkOption}&type=${formType.toLowerCase()}`);
        } else if (selectedOption === "Member State" && memberStateOption) {
          response = await apiService.get(`${url}?country=${countryOption}&type=${formType.toLowerCase()}`);
        } else {
          throw new Error("Invalid selection criteria");
        }

        if (response.statusText !== "OK") {
          throw new Error("Failed to fetch data");
        }

        const fetchedData = response.data;
        setData(fetchedData);

        const totalOngoing = fetchedData.filter(item => item.status === "Ongoing").length;
        const totalPending = fetchedData.filter(item => item.status === "Pending").length;
        const totalCompleted = fetchedData.filter(item => item.status === "Completed").length;

        setProjectSummary({
          totalProjects: fetchedData.length,
          totalOngoing,
          totalPending,
          totalCompleted,
        });
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChartClick = (status) => {
    setSelectedStatus(status);
    const filteredData = data.filter(item => {
      switch (status) {
        case "pending":
          return item.status === "Pending";
        case "ongoing":
          return item.status === "Ongoing";
        case "completed":
          return item.status === "Completed";
        default:
          return true;
      }
    });
    setModalData(filteredData);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  const exportToPPT = () => {
    const pptx = new pptxgen();

    // Slide 1: Project Status Distribution Chart
    const slide1 = pptx.addSlide();
    slide1.addText('Project Status Distribution', { 
      x: 1, 
      y: 0.5, 
      fontSize: 24, 
      color: '000000', 
      fontFace: 'Arial' 
    });

    const summaryText = [
      `Total Projects: ${projectSummary.totalProjects}`,
      `Ongoing Projects(Blue): ${projectSummary.totalOngoing}`,
      `Pending Projects(Orange): ${projectSummary.totalPending}`,
      `Completed Projects(Green): ${projectSummary.totalCompleted}`
    ];

    summaryText.forEach((text, index) => {
      slide1.addText(text, {
        x: 0.5,
        y: 1.5 + index * 0.5,
        fontSize: 16,
        color: '000000'
      });
    });

    slide1.addChart(pptx.ChartType.pie, [
      {
        name: 'Project Status',
        labels: chartData.slice(1).map((item) => item[0]),
        values: chartData.slice(1).map((item) => item[1]),
      },
    ], { 
      x: 3, 
      y: 1.5, 
      w: 6, 
      h: 4,
      showTitle: true,
      titleForm: 'Project Status Breakdown'
    });

    // Slide 2: Project Summary Statistics
    // const slide2 = pptx.addSlide();
    // slide2.addText('Project Summary', { 
    //   x: 1, 
    //   y: 0.5, 
    //   fontSize: 24, 
    //   color: '000000', 
    //   fontFace: 'Arial' 
    // });

    // summaryText.forEach((text, index) => {
    //   slide2.addText(text, {
    //     x: 1,
    //     y: 2 + index * 0.5,
    //     fontSize: 16,
    //     color: '000000'
    //   });
    // });

    // Save the PowerPoint file
    pptx.writeFile({ fileName: 'Project_Data_Analysis.pptx' });
  };

  const chartData = [
    ["Status", "Count"],
    ["Ongoing", projectSummary.totalOngoing || 0],
    ["Pending", projectSummary.totalPending || 0],
    ["Completed", projectSummary.totalCompleted || 0],
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", margin: "auto", overflowX: "auto", flexGrow: 1, paddingRight: "24px" }}>
      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12}>
          <Typography variant="h5" align="center" sx={{ fontWeight: "bold" }}>
            Data Analysis
          </Typography>
          <Divider sx={{ marginBottom: 2 }} />
        </Grid>

        <Grid item xs={12} md={8} color="#000">
          <Box
            sx={{
              border: "1px solid #000",
              borderRadius: "4px",
              padding: "16px",
              marginBottom: "24px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Project Status Distribution
            </Typography>
            <Chart
              chartType="PieChart"
              data={chartData}
              options={{
                pieHole: 0.4,
                slices: {
                  0: { color: "#C9A0DC" },
                  1: { color: "#8806CE" },
                  2: { color: "#645394" },
                },
                legend: {
                  position: 'top',
                  alignment: 'center'
                },
                chartArea: {
                  width: '100%',
                  height: '80%'
                }
              }}
              width="100%"
              height="600px"
              legendToggle
              chartEvents={[
                {
                  eventName: "select",
                  callback: ({ chartWrapper }) => {
                    const chart = chartWrapper.getChart();
                    const selection = chart.getSelection();
                    if (selection.length > 0) {
                      const status = chartData[selection[0].row + 1][0].toLowerCase();
                      handleChartClick(status);
                    }
                  },
                },
              ]}
            />
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={exportToPPT}
          disabled={data.length === 0}
        >
          Export to PowerPoint
        </Button>
      </Box>

      {/* Modal Popup */}
      <Dialog open={modalOpen} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>{`${selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1)} Projects`}</DialogTitle>
        <DialogContent>
          <GridJS
            data={modalData.map(({ name, description, status, startDate, endDate }) => [
              name,
              description,
              status,
              startDate,
              endDate,
            ])}
            columns={["Name", "Description", "Status", "Start Date", "End Date"]}
            search={true}
            pagination={{
              enabled: true,
              limit: 5,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SDataCollection;